import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';
import { Redirect, useHistory } from 'react-router-dom';

import { ReactComponent as CheckCircleSVG } from '../../../../assets/icons/check-circle.svg';
import { activeItemChanged } from '../../../item-details/store';
import {
  clearFormState,
  clearRoutablePoints,
  longPressLocationChanged,
  selectLastSentEditMetadata,
} from '../../../../common/store/item-edit';
import { selectStyleSelectorShown } from '../../../../common/store/ui';
import { compilePath } from '../../../../common/utils/helpers/routes';
import { sendUserSatisfactionFeedback } from '../../../../common/services/api';
import {
  CustomError,
  ErrorType,
  useAppErrors,
} from '../../../../common/contexts/app-error-context';
import { Logger } from '../../../../common/infrastructure/logger';
import { sendReportToSentry } from '../../../../common/utils/helpers/sentry';
import { useMobile } from '../../../../common/components/responsive/Responsive';
import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import UserSatisfactionFeedbackForm from '../user-satisfaction-feedback-form/UserSatisfactionFeedbackForm';
import './EditResultPage.scss';
import { EDIT_SUGGESTION_TYPES } from '../../../../common/utils/constants/constants.ts';

export interface EditResultProps {
  className?: string;
}

const EditResult = ({ className }: EditResultProps) => {
  const isMobile = useMobile();
  const dispatch = useDispatch();
  const history = useHistory();
  const lastSentEditMetadata = useSelector(selectLastSentEditMetadata);
  const { showErrorPage } = useAppErrors();

  useMount(() => {
    dispatch(activeItemChanged({ item: null }));
    dispatch(clearFormState());
    dispatch(clearRoutablePoints());
    dispatch(longPressLocationChanged(null));
  });

  const handleUserSatisfactionFeedbackFormSubmit = useCallback(
    ({ rating, comment }) => {
      try {
        sendUserSatisfactionFeedback({
          feedbackType: lastSentEditMetadata!.feedbackType,
          rating,
          timeSpent: Date.now() - lastSentEditMetadata!.startEditTimestamp,
          comment,
          context: 'session_rating_page',
          editSuggestionId: lastSentEditMetadata!.editSuggestionId,
        });
      } catch (error) {
        const _error: CustomError = error as CustomError;

        sendReportToSentry(_error, {
          origin: 'User satisfaction feedback form',
        });

        Logger.error(
          'Error while sending user satisfaction feedback',
          _error.message,
        );

        showErrorPage({
          type: ErrorType.Internal_API,
        } as CustomError);

        return;
      }

      history.push({
        pathname: compilePath('root'),
        search: history.location.search,
      });
    },
    [history, lastSentEditMetadata, showErrorPage],
  );

  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  if (isMobile && styleSelectorShown) {
    return null;
  }

  if (!lastSentEditMetadata) {
    return (
      <Redirect
        to={{
          pathname: compilePath('root'),
          search: history.location.search,
        }}
      />
    );
  }

  return (
    <WizardLayout
      className={classnames('edit-result', className)}
      hasCloseButton={isMobile}
      fullScreen={true}
    >
      <div className="edit-result__message">
        <div className="edit-result__check-mark">
          <CheckCircleSVG />
        </div>
        <h1 className="edit-result__title">Thank you for your feedback!</h1>
        <div className="edit-result__description">
          {lastSentEditMetadata.featureType === EDIT_SUGGESTION_TYPES.incident
            ? 'Our team will review your issue shortly'
            : `Our team will review your issue shortly and send you an email when it’s verified`}
        </div>
      </div>
      <div className="edit-result__horizontal-divider" />
      <UserSatisfactionFeedbackForm
        className="edit-result__user-satisfaction-feedback-form"
        onSubmit={handleUserSatisfactionFeedbackFormSubmit}
      />
    </WizardLayout>
  );
};

export default EditResult;
